import * as moment from "moment";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { NumericFormat } from "react-number-format";
import { useHistory, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import {
  Block,
  BlockBetween,
  BlockHead,
  BlockHeadContent,
  Button,
  Icon,
} from "../../components/Component";
import Table from "../../components/react-table/Table";
import { GET, POST, PUT } from "../../helper/api";
import { getToken } from "../../helper/auth";
import Content from "../../layout/content/Content";
import Head from "../../layout/head/Head";
import { INGREDIENT_CATEGORY_NAME } from "../../utils/Utils";

const BONUS_KEYS = ["BHD", "DTP", "VNG", "VNB"];

const Num = ({ value, decimalScale = 0 }) => (
  <NumericFormat
    displayType="text"
    value={value ?? 0}
    thousandsGroupStyle="thousand"
    thousandSeparator=","
    decimalScale={decimalScale}
  />
);

const MonthlyCommissionReport = () => {
  const location = useLocation();
  const history = useHistory();
  const searchParams = new URLSearchParams(location.search);
  const debounce = useRef(null);

  const [filter, setFilter] = useState({
    monthYear: searchParams.get("monthYear") || moment().format("YYYY-MM"),
  });

  const [data, setData] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [smOption, setSmOption] = useState(false);
  const [confirmModal, setConfirmModal] = useState(false);
  const [saving, setSaving] = useState(false);

  // Inline editing state cho cột "Cty chi hộ thuế"
  const [editing, setEditing] = useState(null); // { customerId, value }
  const [savingCustomerId, setSavingCustomerId] = useState(null);

  const fetchData = useCallback(() => {
    const params = { monthYear: filter.monthYear };
    GET("/report/monthly-commission", params, true)
      .then(({ data: {
        summary,
        userCommissions,
        customerCommissions,
        isConfirmed,
        isSalaryConfirmed,
      } }) => {
        setData({
          userCommissions: Object.values(userCommissions),
          customerCommissions: Object.values(customerCommissions),
          isConfirmed,
          isSalaryConfirmed,
        })
        setSummary(summary);
      })
      .catch((err) => toast.error(err?.response?.data?.message || "Lỗi tải dữ liệu"))
      .finally(() => setLoading(false));
  }, [filter.monthYear]);

  useEffect(() => {
    const params = { monthYear: filter.monthYear };

    history.replace({
      pathname: location.pathname,
      search: new URLSearchParams(params).toString(),
    });
  }, [filter.monthYear]);
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleConfirm = () => {
    setSaving(true);
    POST("/report/monthly-commission/confirm", { monthYear: filter.monthYear }, {}, true)
      .then(() => {
        toast.success("Chốt hoa hồng thành công!");
        setConfirmModal(false);
        fetchData();
      })
      .catch((err) => toast.error(err?.response?.data?.message || "Lỗi khi chốt hoa hồng"))
      .finally(() => setSaving(false));
  };

  const startEdit = (r) => {
    setEditing({ customerId: r.customerId, value: String(r.companyTaxAmount ?? 0) });
  };

  const commitEdit = (r) => {
    const newVal = Number(editing?.value) || 0;
    const oldVal = r.companyTaxAmount ?? 0;
    setEditing(null);
    if (newVal === oldVal) return;
    if (newVal > r.taxAmount) {
      toast.error("Cty chi hộ thuế không được lớn hơn thuế 10%");
      return;
    }
    setSavingCustomerId(r.customerId);
    PUT(
      "/report/monthly-commission/customer-tax",
      { monthYear: filter.monthYear, customerId: r.customerId, companyTaxAmount: newVal },
      {},
      true
    )
      .then(() => {
        fetchData()
      })
      .catch((err) => toast.error(err?.response?.data?.message || "Lỗi khi lưu"))
      .finally(() => setSavingCustomerId(null));
  };

  const userCommissions = data?.userCommissions || [];
  const customerCommissions = data?.customerCommissions || [];
  const isConfirmed = data?.isConfirmed || false;
  const isSalaryConfirmed = data?.isSalaryConfirmed || false;

  return (
    <React.Fragment>
      <Head title="Hoa hồng tháng" />
      <Content>
        {/* ── Header ── */}
        <BlockHead size="sm">
          <BlockBetween>
            <BlockHeadContent>
              <h4 className="mb-0">
                Báo cáo hoa hồng tháng{" "}
                {moment(filter.monthYear, "YYYY-MM").format("MM/YYYY")}
              </h4>
              <div className="mt-1">
                {isConfirmed ? (
                  <span className="badge badge-success">Đã chốt</span>
                ) : (
                  <span className="badge badge-warning">Chưa chốt</span>
                )}
                {!isSalaryConfirmed && (
                  <span className="badge badge-danger ml-2">Chưa chốt lương</span>
                )}
              </div>
            </BlockHeadContent>
            <BlockHeadContent>
              <div className="toggle-wrap nk-block-tools-toggle">
                <a
                  href="#more"
                  className="btn btn-icon btn-trigger toggle-expand mr-n1"
                  onClick={(e) => {
                    e.preventDefault();
                    setSmOption(!smOption);
                  }}
                >
                  <Icon name="more-v" />
                </a>
                <div
                  className="toggle-expand-content"
                  style={{ display: smOption ? "block" : "none" }}
                >
                  <ul className="d-lg-flex g-3 pt-1 pb-1 custom-filter-header justify-content-end">
                    <li>
                      <input
                        type="month"
                        className="form-control border-primary"
                        style={{ width: 160 }}
                        value={filter.monthYear}
                        onChange={(e) => setFilter({ monthYear: e.target.value })}
                      />
                    </li>
                    <li>
                      <Button
                        color="secondary"
                        size="sm"
                        disabled={loading || !data}
                        onClick={() => {
                          const params = new URLSearchParams({ monthYear: filter.monthYear, token: getToken() });
                          window.open(`${process.env.REACT_APP_API_URL}/api/v1/report/monthly-commission-excel?${params}`, "_blank");
                        }}
                      >
                        <Icon name="download" />
                        <span>Xuất Excel</span>
                      </Button>
                    </li>
                    {!isConfirmed && (
                      <li>
                        <Button
                          color="primary"
                          size="sm"
                          disabled={!isSalaryConfirmed || loading}
                          title={!isSalaryConfirmed ? "Cần chốt lương trước" : ""}
                          onClick={() => setConfirmModal(true)}
                        >
                          <Icon name="check-circle" />
                          <span>Chốt hoa hồng</span>
                        </Button>
                      </li>
                    )}
                  </ul>
                </div>
              </div>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>

        {loading && (
          <div className="text-center py-5">
            <span className="spinner-border spinner-border-sm text-primary" />
            <span className="ml-2">Đang tải...</span>
          </div>
        )}

        {!loading && data && (
          <Block>
            {/* ════════════ BẢNG NHÂN VIÊN ════════════ */}
            <div className="card card-bordered mb-4">
              <div className="card-inner">
                <h6 className="title mb-3">Nhân viên</h6>
                {!isSalaryConfirmed && !isConfirmed && (
                  <div className="alert alert-warning mb-3" style={{ fontSize: 13 }}>
                    <Icon name="alert-circle" /> Chưa chốt lương — thuế TNCN hoa hồng tính được.
                  </div>
                )}
                <Table
                  notShowPagi
                  loading={loading}
                  data={userCommissions}
                  columns={[
                    { Header: "Nhân viên", accessor: (r) => r.user?.name || r.userId },


                    ...BONUS_KEYS.flatMap((key) => [
                      { Header: `KL ${INGREDIENT_CATEGORY_NAME[key] || key} (kg)`, className: "text-right", accessor: (r) => <Num value={r.data?.commissionByIngredient?.[key]?.totalWeight} /> },
                      { Header: `HH ${INGREDIENT_CATEGORY_NAME[key] || key} (đ)`, className: "text-right", accessor: (r) => <Num value={r.data?.commissionByIngredient?.[key]?.totalCommission} /> },
                    ]),

                    { Header: "Tổng HH (đ)", className: "text-right", accessor: (r) => <Num value={r.data?.totalCommission} /> },

                    { Header: "Thu nhập chịu thuế lương (đ)", className: "text-right", accessor: (r) => <Num value={r.data?.salaryData?.taxableIncome} /> },
                    { Header: "Tổng giảm trừ (đ)", className: "text-right", accessor: (r) => <Num value={r.data?.salaryData?.pitDeduction} /> },
                    { Header: "Thu nhập tính thuế (đ)", className: "text-right", accessor: (r) => <Num value={r.data?.salaryData?.assessableIncome} /> },
                    { Header: "TNCN lương (đ)", className: "text-right", accessor: (r) => <Num value={r.data?.salaryData?.pit} /> },
                    { Header: "TNCN hoa hồng (đ)", className: "text-right", accessor: (r) => <Num value={r.taxAmount} /> },
                    { Header: "Thực lãnh (đ)", className: "text-right", accessor: (r) => <strong><Num value={r.netAmount} /></strong> },
                  ]}
                  customRow={() => !!summary?.userCommissions ? (
                    <tr style={{ background: "#f0f4ff", fontWeight: 700 }}>
                      <td colSpan={1 + BONUS_KEYS.length * 2}>Tổng cộng</td>
                      <td className="text-right"><Num value={summary?.userCommissions.data?.totalCommission} /></td>
                      <td className="text-right"><Num value={summary?.userCommissions.data.taxableIncome} /></td>
                      <td className="text-right"><Num value={summary?.userCommissions.data.pitDeduction} /></td>
                      <td className="text-right"><Num value={summary?.userCommissions.data.assessableIncome} /></td>
                      <td className="text-right"><Num value={summary?.userCommissions.data.pit} /></td>
                      <td className="text-right"><Num value={summary?.userCommissions.taxAmount} /></td>
                      <td className="text-right"><Num value={summary?.userCommissions.netAmount} /></td>
                    </tr>
                  ) : null}
                />
              </div>
            </div>

            {/* ════════════ BẢNG NCC / KH ════════════ */}
            <div className="card card-bordered">
              <div className="card-inner">
                <h6 className="title mb-2">Nhà cung cấp / Khách hàng</h6>
                <Table
                  notShowPagi
                  loading={loading}
                  data={customerCommissions}
                  columns={[
                    { Header: "Tên", accessor: (r) => r.customer?.name || r.customerId },
                    { Header: "Hoa hồng (đ)", className: "text-right", accessor: (r) => <Num value={r.data?.totalCommission} /> },
                    { Header: "Thuế 10% (đ)", className: "text-right", accessor: (r) => <Num value={r.taxAmount} /> },
                    {
                      Header: "Cty trả tiền thuế (đ)",
                      className: "text-right",
                      accessor: (r) => {
                        const isEditingThis = editing?.customerId === r.customerId;
                        const isSavingThis = savingCustomerId === r.customerId;
                        if (isConfirmed) return <Num value={r.companyTaxAmount} />;
                        return (
                          <div
                            onClick={(e) => { e.stopPropagation(); !isSavingThis && startEdit(r); }}
                            style={{ cursor: "pointer", userSelect: "none", minWidth: 150 }}
                            title="Nhấn đôi để chỉnh sửa"
                          >
                            {isEditingThis ? (
                              <NumericFormat
                                className="form-control"
                                type="text"
                                value={editing.value}
                                thousandsGroupStyle="thousand"
                                thousandSeparator=","
                                decimalScale={0}
                                onValueChange={(val) => setEditing({ ...editing, value: Math.min(val.floatValue, r.taxAmount) })}
                                onBlur={() => commitEdit(r)}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") commitEdit(r);
                                  if (e.key === "Escape") setEditing(null);
                                }}
                              />
                            ) : isSavingThis ? (
                              <span className="spinner-border spinner-border-sm text-primary" />
                            ) : (
                              <>
                                <Num value={r.companyTaxAmount} />
                                <Icon name="edit" className="ml-1 text-primary" style={{ fontSize: 11, opacity: 0.5 }} />
                              </>
                            )}
                          </div>
                        );
                      },
                    },
                    {
                      Header: "Thực lãnh (đ)",
                      className: "text-right",
                      accessor: (r) => {
                        const isEditingThis = editing?.customerId === r.customerId;
                        const previewNet =
                          (r.data?.totalCommission || 0) -
                          (r.taxAmount || 0) +
                          (isEditingThis ? Number(editing.value) || 0 : r.companyTaxAmount || 0);
                        return <strong><Num value={isEditingThis ? previewNet : r.netAmount} /></strong>;
                      },
                    },
                  ]}
                  customRow={() => !!summary?.customerCommissions ? (
                    <tr style={{ background: "#f0f4ff", fontWeight: 700 }}>
                      <td>Tổng cộng</td>
                      <td className="text-right"><Num value={summary?.customerCommissions.data?.totalCommission} /></td>
                      <td className="text-right"><Num value={summary?.customerCommissions.taxAmount} /></td>
                      <td className="text-right"><Num value={summary?.customerCommissions.companyTaxAmount} /></td>
                      <td className="text-right"><Num value={summary?.customerCommissions.netAmount} /></td>
                    </tr>
                  ) : null}
                />
              </div>
            </div>
          </Block>
        )}

        {/* ── Confirm modal ── */}
        <Modal isOpen={confirmModal} toggle={() => setConfirmModal(false)}>
          <ModalHeader toggle={() => setConfirmModal(false)}>Xác nhận chốt hoa hồng</ModalHeader>
          <ModalBody>
            Bạn có chắc muốn chốt hoa hồng tháng{" "}
            <strong>{moment(filter.monthYear, "YYYY-MM").format("MM/YYYY")}</strong>?
            <br />
            Tiền <em>Cty chi hộ thuế</em> đã nhập sẽ được lưu kèm.
          </ModalBody>
          <ModalFooter>
            <Button color="light" onClick={() => setConfirmModal(false)}>Huỷ</Button>
            <Button color="primary" onClick={handleConfirm} disabled={saving}>
              {saving ? "Đang lưu..." : "Xác nhận chốt"}
            </Button>
          </ModalFooter>
        </Modal>

      </Content>
    </React.Fragment>
  );
};

export default MonthlyCommissionReport;
